import { registerPlugin } from "@capacitor/core";
import { APP_VERSION } from "../config/appConfig";
import { isLiveActivityModuleEnabled, readCachedLiveActivitySettings, LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS } from "./liveActivitySettings";
import { safeLocalStorageSetJson } from "./storageQuotaRecovery";
import { getDeviceId } from "./deviceStorage";

export const LIVE_ACTIVITY_EVENT_TYPES = Object.freeze({
  CREATED: "created",
  STATUS_CHANGED: "status_changed",
  ENDED: "ended",
  CLOSED: "closed",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
});

export const LIVE_ACTIVITY_MODULES = Object.freeze({
  PURCHASES: "purchases",
  SERVICES: "services",
  OCCURRENCES: "occurrences",
  VETERINARY: "veterinary",
});

const LIVE_ACTIVITY_SUPPORTED_MODULES = new Set(Object.values(LIVE_ACTIVITY_MODULES));
const LIVE_ACTIVITY_EVENT_BUFFER_KEY = "fz:liveActivities:eventBuffer:v1";
const LIVE_ACTIVITY_EVENT_BUFFER_LIMIT = 80;
const LIVE_ACTIVITY_PROGRESS_KEY = "fz:liveActivities:progress:v1";
const LIVE_ACTIVITY_SCHEMA_VERSION = 1;
let TremboLiveActivitiesPlugin = null;

function liveDeepLinkDiag(_label, _payload = {}) {
  // v2.221.36: diagnóstico local do Xcode removido.
  // O diagnóstico útil do toque na Live permanece server-side em fz_sync_log.
}

export async function fetchLiveOrderHydration(item = {}) {
  try {
    const { API_SECRET, ORDER_LIVE_HYDRATE_URL, APP_VERSION, MAPS_PLATFORM, isNativeShell: readIsNativeShell } = await import("../config/appConfig");
    if (!ORDER_LIVE_HYDRATE_URL) return { skipped: true, reason: "missing_url" };
    const deviceId = String(item?.deviceId || item?.device_id || (() => { try { return getDeviceId(); } catch (_e) { return ""; } })() || "").trim();
    const userName = String(item?.userName || item?.username || item?.user_name || "").trim();
    const payload = {
      action: "hydrate",
      item: {
        ...(item || {}),
        deviceId,
        device_id: item?.device_id || deviceId,
        appVersion: APP_VERSION,
        platform: MAPS_PLATFORM,
        isNativeShell: typeof readIsNativeShell === "function" ? !!readIsNativeShell() : false,
      },
    };
    const headers = {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
    };
    if (deviceId) headers["X-Device-ID"] = deviceId;
    if (userName) headers["X-User-Name"] = userName;
    const res = await fetch(ORDER_LIVE_HYDRATE_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    let data = null;
    try { data = await res.json(); } catch (_e) { data = null; }
    liveDeepLinkDiag("order-hydrate:response", { ok: res.ok, status: res.status, data });
    return { ok: res.ok, status: res.status, data: data?.data ?? data };
  } catch (error) {
    liveDeepLinkDiag("order-hydrate:error", { error: error?.message || String(error) });
    return { ok: false, error: error?.message || String(error) };
  }
}


// v3.5.01: equivalente de fetchLiveOrderHydration para services.
export async function fetchLiveServiceHydration(item = {}) {
  try {
    const { API_SECRET, SERVICE_LIVE_HYDRATE_URL, APP_VERSION, MAPS_PLATFORM, isNativeShell: readIsNativeShell } = await import("../config/appConfig");
    if (!SERVICE_LIVE_HYDRATE_URL) return { skipped: true, reason: "missing_url" };
    const deviceId = String(item?.deviceId || item?.device_id || (() => { try { return getDeviceId(); } catch (_e) { return ""; } })() || "").trim();
    const userName = String(item?.userName || item?.username || item?.user_name || "").trim();
    const payload = {
      action: "hydrate",
      item: {
        ...(item || {}),
        deviceId,
        device_id: item?.device_id || deviceId,
        appVersion: APP_VERSION,
        platform: MAPS_PLATFORM,
        isNativeShell: typeof readIsNativeShell === "function" ? !!readIsNativeShell() : false,
      },
    };
    const headers = {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
    };
    if (deviceId) headers["X-Device-ID"] = deviceId;
    if (userName) headers["X-User-Name"] = userName;
    const res = await fetch(SERVICE_LIVE_HYDRATE_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    let data = null;
    try { data = await res.json(); } catch (_e) { data = null; }
    liveDeepLinkDiag("service-hydrate:response", { ok: res.ok, status: res.status, data });
    return { ok: res.ok, status: res.status, data: data?.data ?? data };
  } catch (error) {
    liveDeepLinkDiag("service-hydrate:error", { error: error?.message || String(error) });
    return { ok: false, error: error?.message || String(error) };
  }
}

// v3.6.00: equivalente de fetchLiveServiceHydration para occurrences.
export async function fetchLiveOccurrenceHydration(item = {}) {
  try {
    const { API_SECRET, OCCURRENCE_LIVE_HYDRATE_URL, APP_VERSION, MAPS_PLATFORM, isNativeShell: readIsNativeShell } = await import("../config/appConfig");
    if (!OCCURRENCE_LIVE_HYDRATE_URL) return { skipped: true, reason: "missing_url" };
    const deviceId = String(item?.deviceId || item?.device_id || (() => { try { return getDeviceId(); } catch (_e) { return ""; } })() || "").trim();
    const userName = String(item?.userName || item?.username || item?.user_name || "").trim();
    const payload = {
      action: "hydrate",
      item: {
        ...(item || {}),
        deviceId,
        device_id: item?.device_id || deviceId,
        appVersion: APP_VERSION,
        platform: MAPS_PLATFORM,
        isNativeShell: typeof readIsNativeShell === "function" ? !!readIsNativeShell() : false,
      },
    };
    const headers = {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
    };
    if (deviceId) headers["X-Device-ID"] = deviceId;
    if (userName) headers["X-User-Name"] = userName;
    const res = await fetch(OCCURRENCE_LIVE_HYDRATE_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    let data = null;
    try { data = await res.json(); } catch (_e) { data = null; }
    liveDeepLinkDiag("occurrence-hydrate:response", { ok: res.ok, status: res.status, data });
    return { ok: res.ok, status: res.status, data: data?.data ?? data };
  } catch (error) {
    liveDeepLinkDiag("occurrence-hydrate:error", { error: error?.message || String(error) });
    return { ok: false, error: error?.message || String(error) };
  }
}

export async function reportLiveTouchDiagnosticToServer(item = {}) {
  try {
    const { API_SECRET, LIVE_TOUCH_DIAGNOSTIC_URL, APP_VERSION, MAPS_PLATFORM, isNativeShell: readIsNativeShell } = await import("../config/appConfig");
    if (!LIVE_TOUCH_DIAGNOSTIC_URL) return { skipped: true, reason: "missing_url" };
    const payload = {
      action: "log",
      item: {
        ...(item || {}),
        appVersion: APP_VERSION,
        platform: MAPS_PLATFORM,
        isNativeShell: typeof readIsNativeShell === "function" ? !!readIsNativeShell() : false,
      },
    };
    const headers = {
      "Content-Type": "application/json",
      "X-API-Key": API_SECRET,
    };
    const deviceId = String(item?.deviceId || item?.device_id || "").trim();
    const userName = String(item?.userName || item?.username || item?.user_name || "").trim();
    if (deviceId) headers["X-Device-ID"] = deviceId;
    if (userName) headers["X-User-Name"] = userName;
    const res = await fetch(LIVE_TOUCH_DIAGNOSTIC_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    let data = null;
    try { data = await res.json(); } catch (_e) { data = null; }
    liveDeepLinkDiag("server-log:response", { ok: res.ok, status: res.status, data });
    return { ok: res.ok, status: res.status, data };
  } catch (error) {
    liveDeepLinkDiag("server-log:error", { error: error?.message || String(error) });
    return { ok: false, error: error?.message || String(error) };
  }
}

const PURCHASE_LIVE_STEP_RANKS = Object.freeze({
  "criado": 1,
  "pendente": 1,
  "cotacao": 2,
  "cotação": 2,
  "em analise": 2,
  "em análise": 2,
  "analisando": 2,
  "aprovado": 3,
  "aprovada": 3,
  "parcial": 3,
  "enviado": 4,
  "enviada": 4,
  "em transito": 4,
  "em trânsito": 4,
  "recebido": 5,
  "recebida": 5,
  "concluido": 5,
  "concluído": 5,
  "concluida": 5,
  "concluída": 5,
});

const LIVE_ACTIVITY_FINAL_STATUS = new Set([
  "finalizado",
  "finalizada",
  "concluido",
  "concluida",
  "concluído",
  "concluída",
  "cancelado",
  "cancelada",
  "encerrado",
  "encerrada",
  "resolvido",
  "resolvida",
  "completed",
  "complete",
  "cancelled",
  "canceled",
  "closed",
  "ended",
  "rejected",
  "resolved",
]);

function normalizeText(value, fallback = "") {
  const text = typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
  return text || fallback;
}

function normalizeModule(moduleName) {
  const value = normalizeText(moduleName).toLowerCase();
  if (value === "orders") return LIVE_ACTIVITY_MODULES.PURCHASES;
  if (value === "vet") return LIVE_ACTIVITY_MODULES.VETERINARY;
  if (LIVE_ACTIVITY_SUPPORTED_MODULES.has(value)) return value;
  return value || "operational";
}

function normalizeStepKey(value) {
  return normalizeText(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function purchaseLiveStepRank(value) {
  const key = normalizeStepKey(value);
  if (!key) return 0;
  if (Object.prototype.hasOwnProperty.call(PURCHASE_LIVE_STEP_RANKS, key)) return PURCHASE_LIVE_STEP_RANKS[key];
  if (key.includes("receb") || key.includes("conclu")) return 5;
  if (key.includes("envi") || key.includes("transito")) return 4;
  if (key.includes("aprov") || key.includes("parcial")) return 3;
  if (key.includes("cot") || key.includes("analis")) return 2;
  if (key.includes("criad") || key.includes("pend")) return 1;
  return 0;
}

function purchaseLiveStepLabel(rank, fallback = "") {
  if (rank >= 5) return "Recebido";
  if (rank >= 4) return "Enviado";
  if (rank >= 3) return "Aprovado";
  if (rank >= 2) return "Cotação";
  return normalizeText(fallback, "Criado");
}

function readLiveActivityProgressMap() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const parsed = JSON.parse(window.localStorage.getItem(LIVE_ACTIVITY_PROGRESS_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function writeLiveActivityProgressMap(map) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const entries = Object.entries(map || {}).slice(-120);
    window.localStorage.setItem(LIVE_ACTIVITY_PROGRESS_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch (_error) {}
}

function applyMonotonicPurchaseLiveStepper(payload = {}) {
  const safe = payload && typeof payload === "object" ? { ...payload } : {};
  if (normalizeModule(safe.module) !== LIVE_ACTIVITY_MODULES.PURCHASES) return safe;
  const activityKey = normalizeText(safe.activityKey);
  if (!activityKey) return safe;
  // v2.218.04: displayBadge pode conter itemStatus "Analisando" dos itens do pedido,
  // que não representa avanço real do pedido para Cotação.
  // Só usar displayBadge/extra.displayBadge se rank >= 3 (Aprovado/Enviado/Recebido).
  const badgeRank = purchaseLiveStepRank(safe.displayBadge);
  const extraBadgeRank = purchaseLiveStepRank(safe.extra?.displayBadge);
  const rawRank = Math.max(
    purchaseLiveStepRank(safe.stepperCurrent),
    purchaseLiveStepRank(safe.status),
    badgeRank >= 3 ? badgeRank : 0,
    purchaseLiveStepRank(safe.extra?.stepperCurrent),
    extraBadgeRank >= 3 ? extraBadgeRank : 0
  );
  if (!rawRank) return safe;
  const map = readLiveActivityProgressMap();
  const previous = map[activityKey] && typeof map[activityKey] === "object" ? map[activityKey] : null;
  const previousRank = Number(previous?.rank || 0);
  const finalRank = Math.max(rawRank, previousRank);
  const finalLabel = rawRank < previousRank ? purchaseLiveStepLabel(finalRank, previous?.label || safe.stepperCurrent) : purchaseLiveStepLabel(finalRank, safe.stepperCurrent);
  const next = { ...safe, stepperCurrent: finalLabel };
  if (next.extra && typeof next.extra === "object") next.extra = { ...next.extra, stepperCurrent: finalLabel };
  map[activityKey] = { rank: finalRank, label: finalLabel, updatedAt: new Date().toISOString() };
  writeLiveActivityProgressMap(map);
  return next;
}

function normalizeStatus(value) {
  return normalizeText(value, "pendente");
}

function liveActivityModuleLabel(moduleName) {
  const module = normalizeText(moduleName).toLowerCase();
  if (module === "orders" || module === "purchases") return "Compras";
  if (module === "services") return "Serviços";
  if (module === "occurrences") return "Ocorrências";
  if (module === "vet" || module === "veterinary") return "Veterinário";
  return "Atividade operacional";
}

function prefixTremboLiveActivityTitle(value, moduleName) {
  const raw = normalizeText(value);
  const module = normalizeText(moduleName).toLowerCase();
  const moduleLabel = liveActivityModuleLabel(moduleName);
  const generic = !raw || /^atividade operacional$/i.test(raw) || /^trembo\s*-\s*atividade operacional$/i.test(raw);
  if ((module === "orders" || module === "purchases") && /^trembo\s*-\s*pedido\b/i.test(raw)) return "Trembo - Compras";
  if ((module === "orders" || module === "purchases") && /^pedido\b/i.test(raw)) return "Trembo - Compras";
  if ((module === "vet" || module === "veterinary") && /^trembo\s*-\s*atendimento\b/i.test(raw)) return "Trembo - Veterinário";
  if ((module === "vet" || module === "veterinary") && /^atendimento\b/i.test(raw)) return "Trembo - Veterinário";
  const title = generic ? moduleLabel : raw;
  if (/^trembo\s*-/i.test(title)) return title;
  return `Trembo - ${title}`;
}

function normalizePriority(value) {
  const priority = normalizeText(value, "normal").toLowerCase();
  if (["low", "baixa"].includes(priority)) return "low";
  if (["high", "alta", "urgent", "urgente", "emergency", "emergencia", "emergência", "critical", "critica", "crítica"].includes(priority)) return "high";
  if (["medium", "media", "média"].includes(priority)) return "medium";
  return "normal";
}

function readCapacitorPlugin() {
  try {
    if (typeof window === "undefined") return null;
    if (!window.Capacitor) return null;
    if (!window.Capacitor.isNativePlatform?.()) return null;
    if (!TremboLiveActivitiesPlugin) {
      TremboLiveActivitiesPlugin = registerPlugin("TremboLiveActivities");
    }
    return TremboLiveActivitiesPlugin || null;
  } catch (_error) {
    return null;
  }
}

function readLegacyBridge() {
  try {
    const win = typeof window !== "undefined" ? window : null;
    if (!win) return null;
    return win.TremboLiveActivities || win.LiveActivities || win.webkit?.messageHandlers?.tremboLiveActivities || null;
  } catch (_error) {
    return null;
  }
}

function readNativeBridge() {
  return readCapacitorPlugin() || readLegacyBridge();
}

function detectNativePlatform() {
  try {
    const win = typeof window !== "undefined" ? window : null;
    const platform = win?.Capacitor?.getPlatform?.();
    if (platform) return String(platform).toLowerCase();
    const ua = String(win?.navigator?.userAgent || "").toLowerCase();
    if (/iphone|ipad|ipod|ios/.test(ua)) return "ios";
    if (/android/.test(ua)) return "android";
  } catch (_error) {}
  return "web";
}

function isCapacitorAvailable() {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor);
  } catch (_error) {
    return false;
  }
}

export function isNativeShell() {
  try {
    return isCapacitorAvailable() && detectNativePlatform() === "ios";
  } catch (_error) {
    return false;
  }
}

export function isLiveActivitiesAvailable() {
  return !!readNativeBridge();
}

export function parseLiveActivityDeepLink(input) {
  const raw = normalizeText(typeof input === "string" ? input : (input?.url || input?.href || ""));
  liveDeepLinkDiag("parse:input", { raw, input });
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const pathParts = String(url.pathname || "").split("/").filter(Boolean).map((part) => {
      try { return decodeURIComponent(part); } catch (_e) { return part; }
    });
    const host = String(url.host || "").trim().toLowerCase();
    const params = url.searchParams;
    let module = normalizeText(params.get("module") || params.get("collection") || "");
    let recordId = normalizeText(params.get("recordId") || params.get("orderId") || params.get("id") || "");
    let farmId = normalizeText(params.get("farmId") || params.get("farm") || "");
    let activityKey = normalizeText(params.get("activityKey") || params.get("activity_key") || "");

    // Formato principal usado pelo widget:
    // trembo://live/{farmId}:{module}:{recordId}
    if ((host === "live" || host === "open") && !activityKey && pathParts.length >= 1) {
      activityKey = normalizeText(pathParts[0]);
    }

    if (activityKey) {
      const pieces = activityKey.split(":").map((piece) => normalizeText(piece));
      if (!farmId && pieces.length >= 3) farmId = pieces[0];
      if (!module && pieces.length >= 3) module = pieces[1];
      if (!recordId && pieces.length >= 3) recordId = pieces.slice(2).join(":");
      if (!recordId && pieces.length >= 2) recordId = pieces[pieces.length - 1];
    }

    // Formatos alternativos:
    // trembo://purchases/{orderId}
    // trembo://open/purchases/{orderId}?farmId=...
    if (!module && host && host !== "live" && host !== "open") {
      module = host;
    }
    if (!recordId && pathParts.length >= 2) {
      if (!module || module === "open") module = normalizeText(pathParts[0]);
      recordId = normalizeText(pathParts[1]);
    }

    const normalizedModule = normalizeModule(module || "purchases");
    const finalRecordId = recordId || normalizeText(activityKey.split(":").pop() || "");
    if (!finalRecordId) return null;
    const parsed = {
      source: "live-activity",
      url: raw,
      module: normalizedModule,
      recordId: finalRecordId,
      orderId: normalizedModule === LIVE_ACTIVITY_MODULES.PURCHASES ? finalRecordId : "",
      farmId,
      activityKey,
      receivedAt: normalizeText(input?.receivedAt, new Date().toISOString()),
    };
    liveDeepLinkDiag("parse:parsed", parsed);
    return parsed;
  } catch (error) {
    liveDeepLinkDiag("parse:error", { raw, error: error?.message || String(error) });
    return null;
  }
}


export async function getPendingLiveActivityDeepLink() {
  const bridge = readNativeBridge();
  if (!bridge || typeof bridge.getPendingLiveActivityDeepLink !== "function") return null;
  try {
    const result = await bridge.getPendingLiveActivityDeepLink({});
    liveDeepLinkDiag("bridge:getPending:raw", result);
    return parseLiveActivityDeepLink(result);
  } catch (error) {
    liveDeepLinkDiag("bridge:getPending:error", { error: error?.message || String(error) });
    return null;
  }
}

export async function clearPendingLiveActivityDeepLink() {
  const bridge = readNativeBridge();
  if (!bridge || typeof bridge.clearPendingLiveActivityDeepLink !== "function") return { skipped: true };
  try {
    return await bridge.clearPendingLiveActivityDeepLink({});
  } catch (error) {
    return { ok: false, error: error?.message || String(error) };
  }
}

export function subscribeLiveActivityDeepLink(handler) {
  const bridge = readNativeBridge();
  if (!bridge || typeof bridge.addListener !== "function" || typeof handler !== "function") return null;
  try {
    const sub = bridge.addListener("liveActivityDeepLink", (event) => {
      liveDeepLinkDiag("bridge:listener:raw", event);
      const parsed = parseLiveActivityDeepLink(event);
      if (parsed) handler(parsed);
    });
    return sub;
  } catch (_error) {
    return null;
  }
}

function buildActivityKey(moduleName, recordId, clientId, farmId) {
  const safeFarm = normalizeText(farmId, "sem-fazenda");
  const normalizedModule = normalizeModule(moduleName);
  // v2.220.01: chave canônica de Compras usa a coleção real do sync/API (orders),
  // não o nome visual do módulo (purchases). Isso alinha start/update remoto e local.
  const safeModule = normalizedModule === LIVE_ACTIVITY_MODULES.PURCHASES ? "orders" : normalizedModule;
  // v2.220.00: clientId tem prioridade — é criado offline e estável antes do sync.
  // recordId (server id) pode chegar depois; order_number é instável.
  const safeId = normalizeText(clientId || recordId, "sem-id");
  return `${safeFarm}:${safeModule}:${safeId}`;
}

function isFinalStatus(status) {
  const normalized = normalizeText(status).toLowerCase();
  return LIVE_ACTIVITY_FINAL_STATUS.has(normalized);
}

export function buildOperationalLiveActivityPayload({
  module,
  eventType,
  record,
  recordId,
  clientId,
  title,
  status,
  previousStatus,
  farmId,
  farmName,
  farmShortCode,
  extraFarmShortCode,
  priority,
  updatedAt,
  actor,
  displayTitle,
  displayBadge,
  recordCode,
  displayDate,
  costCenterName,
  stepperLabels,
  stepperCurrent,
  extra,
} = {}) {
  const safeRecord = record && typeof record === "object" ? record : {};
  const resolvedRecordId = normalizeText(recordId || safeRecord.id || safeRecord.clientId || clientId);
  const resolvedClientId = normalizeText(clientId || safeRecord.clientId || safeRecord.id || resolvedRecordId);
  const resolvedModule = normalizeModule(module || safeRecord.module || safeRecord.collection);
  const resolvedEventType = normalizeText(eventType, LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED);
  const resolvedTitle = normalizeText(
    title || safeRecord.title || safeRecord.name || safeRecord.description || safeRecord.summary,
    "Atividade operacional"
  );
  const resolvedFarmId = normalizeText(farmId || safeRecord.farmId || safeRecord.farm_id);
  const resolvedStatus = normalizeStatus(status || safeRecord.status || safeRecord.state);
  const sourceExtra = extra && typeof extra === "object" ? extra : {};
  const resolvedStepperLabels = Array.isArray(stepperLabels)
    ? stepperLabels
    : (Array.isArray(sourceExtra.stepperLabels) ? sourceExtra.stepperLabels : (Array.isArray(safeRecord.stepperLabels) ? safeRecord.stepperLabels : []));
  const liveSettings = readCachedLiveActivitySettings();
  const maxSimultaneousActivities = Math.max(1, Math.min(7, Math.trunc(Number(liveSettings?.maxSimultaneousActivities || LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS))));
  const resolvedFarmShortCode = normalizeText(extraFarmShortCode || farmShortCode || sourceExtra.farmShortCode || sourceExtra.farmSigla || safeRecord.farmShortCode || safeRecord.farmSigla || safeRecord.farm_short_code || safeRecord.farm_sigla);
  const rawCostCenterName = normalizeText(costCenterName || sourceExtra.costCenterName || safeRecord.costCenterName || safeRecord.costCenter || safeRecord.cost_center);
  const resolvedCostCenterName = resolvedFarmShortCode && rawCostCenterName && !rawCostCenterName.startsWith(`${resolvedFarmShortCode} ·`) ? `${resolvedFarmShortCode} · ${rawCostCenterName}` : rawCostCenterName;
  const nowIso = new Date().toISOString();

  return {
    schemaVersion: LIVE_ACTIVITY_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    platform: detectNativePlatform(),
    source: "trembo-web",
    maxSimultaneousActivities,
    module: resolvedModule,
    eventType: resolvedEventType,
    recordId: resolvedRecordId,
    clientId: resolvedClientId,
    activityKey: buildActivityKey(resolvedModule, resolvedRecordId, resolvedClientId, resolvedFarmId),
    title: resolvedTitle,
    status: resolvedStatus,
    previousStatus: normalizeText(previousStatus || safeRecord.previousStatus),
    farmId: resolvedFarmId,
    farmName: normalizeText(farmName || safeRecord.farmName || safeRecord.farm_name),
    farmShortCode: resolvedFarmShortCode,
    priority: normalizePriority(priority || safeRecord.priority),
    updatedAt: normalizeText(updatedAt || safeRecord.updatedAt || safeRecord.createdAt, nowIso),
    actor: normalizeText(actor || safeRecord.updatedBy || safeRecord.createdBy),
    displayTitle: prefixTremboLiveActivityTitle(displayTitle || sourceExtra.displayTitle || safeRecord.displayTitle || title || safeRecord.title || safeRecord.name || safeRecord.description || safeRecord.summary || resolvedTitle, resolvedModule),
    displayBadge: normalizeText(displayBadge || sourceExtra.displayBadge || safeRecord.displayBadge || status || safeRecord.displayStatus || safeRecord.itemStatus || safeRecord.status || safeRecord.state, resolvedStatus),
    recordCode: normalizeText(recordCode || sourceExtra.recordCode || sourceExtra.recordNumber || safeRecord.recordCode || safeRecord.recordNumber || safeRecord.code || resolvedRecordId),
    displayDate: normalizeText(displayDate || sourceExtra.displayDate || safeRecord.displayDate || safeRecord.date || safeRecord.createdAt || safeRecord.updatedAt),
    costCenterName: resolvedCostCenterName,
    stepperLabels: resolvedStepperLabels.map(label => normalizeText(label)).filter(Boolean).slice(0, 6),
    stepperCurrent: normalizeText(stepperCurrent || sourceExtra.stepperCurrent || sourceExtra.stepperStage || safeRecord.stepperCurrent || safeRecord.stepperStage || resolvedStatus),
    extra: sourceExtra,
  };
}

function sanitizePayloadForNative(payload = {}) {
  const safe = payload && typeof payload === "object" ? payload : {};
  return {
    ...safe,
    schemaVersion: Number(safe.schemaVersion || LIVE_ACTIVITY_SCHEMA_VERSION),
    appVersion: normalizeText(safe.appVersion, APP_VERSION),
    platform: detectNativePlatform(),
    source: normalizeText(safe.source, "trembo-web"),
    maxSimultaneousActivities: Math.max(1, Math.min(7, Math.trunc(Number(safe.maxSimultaneousActivities || LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS)))),
    module: normalizeModule(safe.module),
    eventType: normalizeText(safe.eventType, LIVE_ACTIVITY_EVENT_TYPES.STATUS_CHANGED),
    recordId: normalizeText(safe.recordId || safe.clientId, "sem-id"),
    clientId: normalizeText(safe.clientId || safe.recordId, "sem-id"),
    activityKey: normalizeText(safe.activityKey, buildActivityKey(safe.module, safe.recordId, safe.clientId, safe.farmId)),
    title: normalizeText(safe.title, "Atividade operacional"),
    status: normalizeStatus(safe.status),
    previousStatus: normalizeText(safe.previousStatus),
    farmId: normalizeText(safe.farmId),
    farmName: normalizeText(safe.farmName),
    farmShortCode: normalizeText(safe.farmShortCode),
    priority: normalizePriority(safe.priority),
    updatedAt: normalizeText(safe.updatedAt, new Date().toISOString()),
    actor: normalizeText(safe.actor),
    displayTitle: prefixTremboLiveActivityTitle(safe.displayTitle || safe.title || "Atividade operacional", safe.module),
    displayBadge: normalizeText(safe.displayBadge, normalizeStatus(safe.status)),
    recordCode: normalizeText(safe.recordCode, normalizeText(safe.recordId || safe.clientId, "sem-id")),
    displayDate: normalizeText(safe.displayDate),
    costCenterName: normalizeText(safe.costCenterName),
    stepperLabels: Array.isArray(safe.stepperLabels) ? safe.stepperLabels.map(label => normalizeText(label)).filter(Boolean).slice(0, 6) : [],
    stepperCurrent: normalizeText(safe.stepperCurrent, normalizeStatus(safe.status)),
    extra: safe.extra && typeof safe.extra === "object" ? safe.extra : {},
  };
}

function saveBufferedEvent(type, payload) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(LIVE_ACTIVITY_EVENT_BUFFER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const base = Array.isArray(parsed) ? parsed : [];
    base.push({ type, payload, bufferedAt: new Date().toISOString(), nativePlatform: detectNativePlatform() });
    window.localStorage.setItem(
      LIVE_ACTIVITY_EVENT_BUFFER_KEY,
      JSON.stringify(base.slice(-LIVE_ACTIVITY_EVENT_BUFFER_LIMIT))
    );
  } catch (_error) {}
}

async function callNative(type, payload, options = {}) {
  const { bufferIfUnavailable = true, bufferErrors = true, sanitize = true } = options || {};
  const nativePayload = sanitize ? sanitizePayloadForNative(payload) : (payload && typeof payload === "object" ? payload : {});
  const bridge = readNativeBridge();
  if (!bridge) {
    if (bufferIfUnavailable) saveBufferedEvent(type, nativePayload);
    return { supported: false, queued: !!bufferIfUnavailable, platform: detectNativePlatform(), capacitor: isCapacitorAvailable() };
  }
  try {
    if (typeof bridge[type] === "function") {
      const result = await bridge[type](nativePayload);
      return { supported: true, result, platform: detectNativePlatform(), capacitor: isCapacitorAvailable() };
    }
    if (typeof bridge.postMessage === "function") {
      bridge.postMessage({ type, payload: nativePayload });
      return { supported: true, posted: true, platform: detectNativePlatform(), capacitor: isCapacitorAvailable() };
    }
  } catch (error) {
    if (bufferErrors) {
      saveBufferedEvent(`${type}:error`, {
        ...nativePayload,
        nativeError: error?.message || String(error),
      });
    }
    return { supported: false, error: error?.message || String(error), platform: detectNativePlatform(), capacitor: isCapacitorAvailable() };
  }
  if (bufferIfUnavailable) saveBufferedEvent(type, nativePayload);
  return { supported: false, queued: !!bufferIfUnavailable, platform: detectNativePlatform(), capacitor: isCapacitorAvailable() };
}

export async function startLiveActivity(payload = {}) {
  return callNative("startLiveActivity", payload);
}

export async function updateLiveActivity(payload = {}) {
  return callNative("updateLiveActivity", payload);
}

export async function endLiveActivity(payload = {}) {
  return callNative("endLiveActivity", payload);
}

export async function endAllLiveActivities(payload = {}) {
  const result = await callNative("endAllLiveActivities", {
    title: "Trembo",
    displayTitle: "Trembo",
    status: "Encerrada",
    displayBadge: "Encerrada",
    reason: "admin-clear-all",
    ...payload,
  }, { bufferIfUnavailable: false, bufferErrors: false });
  clearBufferedLiveActivityEvents();
  // Notify backend to clear stale activity push tokens — prevents update/end HTTP 400
  // on tokens that belonged to activities that just ended.
  try { await clearActivityTokensOnBackend(payload); } catch (_) {}
  return result;
}

/**
 * Clears activity_push_token and activity_push_tokens_json on the backend for this device/farm.
 * Called after endAll so the backend stops trying update/end with stale tokens.
 * The push_to_start_token is preserved (still valid for next activity start).
 */
export async function clearActivityTokensOnBackend(payload = {}) {
  try {
    const { API_SECRET, LIVE_ACTIVITY_DEVICE_URL } = await import("../config/appConfig");
    const farmId = String(payload?.farmId || "").trim();
    const deviceId = (() => {
      try { return String(window?.localStorage?.getItem?.("fz:device_id") || "").trim(); } catch (_) { return ""; }
    })();
    if (!farmId || !deviceId) return { skipped: true, reason: "missing_identity" };
    const response = await fetch(LIVE_ACTIVITY_DEVICE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-API-Key": API_SECRET },
      body: JSON.stringify({ action: "clear_activity_tokens", farm_id: farmId, device_id: deviceId }),
    });
    return response.ok ? { cleared: true } : { cleared: false, status: response.status };
  } catch (e) {
    return { cleared: false, error: e?.message };
  }
}

export async function requestRemoteLiveActivityAuthorization(payload = {}) {
  const statusPayload = payload && typeof payload === "object" ? payload : {};
  const bridge = readNativeBridge();
  const base = { platform: detectNativePlatform(), capacitor: isCapacitorAvailable(), remoteToken: true };
  if (!bridge || typeof bridge.requestRemoteLiveActivityAuthorization !== "function") return { ...base, supported: false, bridgeReady: !!bridge, reason: "method_unavailable" };
  try {
    const result = await bridge.requestRemoteLiveActivityAuthorization(statusPayload);
    return result && typeof result === "object" ? { ...base, ...result, bridgeReady: result.bridgeReady !== false } : { ...base, supported: true, bridgeReady: true, result };
  } catch (error) {
    return { ...base, supported: false, bridgeReady: true, error: error?.message || String(error) };
  }
}

export async function startRemoteLiveActivityTokenUpdates(payload = {}) {
  const statusPayload = payload && typeof payload === "object" ? payload : {};
  const bridge = readNativeBridge();
  const base = { platform: detectNativePlatform(), capacitor: isCapacitorAvailable(), remoteToken: true };
  if (!bridge || typeof bridge.startRemoteLiveActivityTokenUpdates !== "function") return { ...base, supported: false, bridgeReady: !!bridge, reason: "method_unavailable" };
  try {
    const result = await bridge.startRemoteLiveActivityTokenUpdates(statusPayload);
    return result && typeof result === "object" ? { ...base, ...result, bridgeReady: result.bridgeReady !== false } : { ...base, supported: true, bridgeReady: true, result };
  } catch (error) {
    return { ...base, supported: false, bridgeReady: true, error: error?.message || String(error) };
  }
}

export async function persistBackendCredentials(payload = {}) {
  const statusPayload = payload && typeof payload === "object" ? payload : {};
  const bridge = readNativeBridge();
  const base = { platform: detectNativePlatform(), capacitor: isCapacitorAvailable(), backendCredentials: true };
  if (!bridge || typeof bridge.persistBackendCredentials !== "function") return { ...base, supported: false, bridgeReady: !!bridge, reason: "method_unavailable" };
  try {
    const result = await bridge.persistBackendCredentials(statusPayload);
    return result && typeof result === "object" ? { ...base, ...result, bridgeReady: true } : { ...base, supported: true, bridgeReady: true, result };
  } catch (error) {
    return { ...base, supported: false, bridgeReady: true, error: error?.message || String(error) };
  }
}

export async function getRemoteLiveActivityTokenStatus(payload = {}) {
  const statusPayload = payload && typeof payload === "object" ? payload : {};
  const bridge = readNativeBridge();
  const base = { platform: detectNativePlatform(), capacitor: isCapacitorAvailable(), remoteToken: true };
  if (!bridge || typeof bridge.getRemoteLiveActivityTokenStatus !== "function") return { ...base, supported: false, bridgeReady: !!bridge, reason: "method_unavailable" };
  try {
    const result = await bridge.getRemoteLiveActivityTokenStatus(statusPayload);
    return result && typeof result === "object" ? { ...base, ...result, bridgeReady: result.bridgeReady !== false } : { ...base, supported: true, bridgeReady: true, result };
  } catch (error) {
    return { ...base, supported: false, bridgeReady: true, error: error?.message || String(error) };
  }
}

export async function getLiveActivityStatus(payload = {}) {
  const statusPayload = payload && typeof payload === "object" ? payload : {};
  const bridge = readNativeBridge();
  const base = {
    platform: detectNativePlatform(),
    capacitor: isCapacitorAvailable(),
    diagnostic: true,
  };

  if (!bridge) {
    return { ...base, supported: false, queued: false, bridgeReady: false, reason: "native_bridge_unavailable" };
  }

  try {
    if (typeof bridge.getLiveActivityStatus === "function") {
      const result = await bridge.getLiveActivityStatus(statusPayload);
      return result && typeof result === "object"
        ? { ...result, ...base, bridgeReady: result.bridgeReady !== false }
        : { ...base, supported: true, bridgeReady: true, result };
    }

    if (typeof bridge.postMessage === "function") {
      bridge.postMessage({ type: "getLiveActivityStatus", payload: statusPayload });
      return { ...base, supported: true, bridgeReady: true, posted: true };
    }
  } catch (error) {
    return {
      ...base,
      supported: false,
      bridgeReady: true,
      diagnosticError: true,
      error: error?.message || String(error),
    };
  }

  return { ...base, supported: false, queued: false, bridgeReady: false, reason: "method_unavailable" };
}

export function getLiveActivityRuntimeSnapshot(nativeStatus = null) {
  const bufferedEvents = getBufferedLiveActivityEvents();
  const lastBuffered = bufferedEvents.length ? bufferedEvents[bufferedEvents.length - 1] : null;
  const nativeResponded = !!(nativeStatus && !nativeStatus.error && (nativeStatus.supported || nativeStatus.bridgeReady));
  return {
    checkedAt: new Date().toISOString(),
    available: nativeStatus ? nativeResponded : isLiveActivitiesAvailable(),
    nativeResponded,
    capacitor: isCapacitorAvailable(),
    platform: detectNativePlatform(),
    bufferLimit: LIVE_ACTIVITY_EVENT_BUFFER_LIMIT,
    bufferedCount: bufferedEvents.length,
    lastBufferedAt: lastBuffered?.bufferedAt || "",
    nativeStatus: nativeStatus || null,
    error: nativeStatus?.error || "",
    bufferedEvents,
  };
}

export async function readLiveActivityDiagnostics() {
  const base = getLiveActivityRuntimeSnapshot();
  if (!base.available) return base;
  try {
    const nativeStatus = await getLiveActivityStatus({ requestedAt: new Date().toISOString() });
    let remoteTokenStatus = null;
    try {
      remoteTokenStatus = await getRemoteLiveActivityTokenStatus({ requestedAt: new Date().toISOString() });
      if (remoteTokenStatus?.supported && remoteTokenStatus?.remotePushSupported) {
        remoteTokenStatus = await startRemoteLiveActivityTokenUpdates({ requestedAt: new Date().toISOString() });
      }
    } catch (_error) {}
    return { ...getLiveActivityRuntimeSnapshot(nativeStatus), remoteTokenStatus };
  } catch (error) {
    return getLiveActivityRuntimeSnapshot({ supported: true, error: error?.message || String(error) });
  }
}

export function notifyOperationalActivity(input = {}) {
  let payload = buildOperationalLiveActivityPayload(input);
  if (input?.activityKey && payload.activityKey !== input.activityKey) {
    payload.activityKey = input.activityKey;
  }
  // v2.219.00: criação de Compras deve nascer sempre em Criado. ItemStatus
  // "Analisando" dos itens não é abertura por destinatário nem avanço real.
  // Também limpa eventual progresso local antigo para a mesma activityKey antes
  // do start, evitando que um cache local antigo force Cotação na criação.
  const createdPurchase = normalizeModule(payload.module) === LIVE_ACTIVITY_MODULES.PURCHASES
    && String(payload.eventType || "").toLowerCase() === LIVE_ACTIVITY_EVENT_TYPES.CREATED;
  if (createdPurchase) {
    const hasRecipientOpen = !!(input?.record?.firstRecipientOpenedAt || input?.record?.analysisStartedAt);
    const normalizedStatus = normalizeStepKey(payload.status || input?.record?.status || "");
    const stillInitial = !hasRecipientOpen && (!normalizedStatus || normalizedStatus === "pendente" || normalizedStatus === "criado");
    if (stillInitial) {
      try {
        const map = readLiveActivityProgressMap();
        if (payload.activityKey && Object.prototype.hasOwnProperty.call(map, payload.activityKey)) {
          delete map[payload.activityKey];
          writeLiveActivityProgressMap(map);
        }
      } catch (_error) {}
      payload = {
        ...payload,
        status: "Criado",
        displayBadge: "Criado",
        stepperCurrent: "Criado",
        extra: payload.extra && typeof payload.extra === "object" ? { ...payload.extra, displayBadge: "Criado", stepperCurrent: "Criado" } : payload.extra,
      };
    }
  }
  payload = applyMonotonicPurchaseLiveStepper(payload);
  if (!isLiveActivityModuleEnabled(readCachedLiveActivitySettings(), payload.module)) {
    return { supported: false, disabled: true };
  }
  const eventType = String(payload.eventType || "").toLowerCase();
  if (eventType === LIVE_ACTIVITY_EVENT_TYPES.CREATED) {
    return startLiveActivity(payload);
  }
  if ([
    LIVE_ACTIVITY_EVENT_TYPES.ENDED,
    LIVE_ACTIVITY_EVENT_TYPES.CLOSED,
    LIVE_ACTIVITY_EVENT_TYPES.COMPLETED,
    LIVE_ACTIVITY_EVENT_TYPES.CANCELLED,
  ].includes(eventType) || isFinalStatus(payload.status)) {
    return endLiveActivity(payload);
  }
  return updateLiveActivity(payload);
}

const COLLECTION_TO_MODULE = Object.freeze({
  orders: LIVE_ACTIVITY_MODULES.PURCHASES,
  services: LIVE_ACTIVITY_MODULES.SERVICES,
  occurrences: LIVE_ACTIVITY_MODULES.OCCURRENCES,
  vet: LIVE_ACTIVITY_MODULES.VETERINARY,
});

const TERMINAL_STATUS_FRAGMENTS = [
  "concluí",
  "concluido",
  "cancelad",
  "recebid",
  "rejeitad",
  "resolvi",
  "encerrad",
  "finalizad",
];

function isTerminalStatusText(status) {
  const s = String(status || "").toLowerCase().trim();
  return TERMINAL_STATUS_FRAGMENTS.some(fragment => s.startsWith(fragment));
}

function readRecordIdentity(record) {
  if (!record || typeof record !== "object") return "";
  // v2.220.00: clientId tem prioridade — nasce offline, é estável antes do sync.
  return normalizeText(record.clientId || record.client_id || record.id || record.recordId);
}

function readRecordStatus(record) {
  if (!record || typeof record !== "object") return "";
  return normalizeText(record.status || record.itemStatus || record.displayStatus || record.state);
}

function readRecordActorName(record, fields = []) {
  if (!record || typeof record !== "object") return "";
  for (const field of fields) {
    const value = record[field];
    if (value && typeof value === "object") {
      const nested = normalizeText(value.name || value.nome || value.fullName || value.displayName);
      if (nested) return nested;
    }
    const text = normalizeText(value);
    if (text) return text;
  }
  return "";
}

function sameActorName(a, b) {
  const left = normalizeText(a).toLowerCase();
  const right = normalizeText(b).toLowerCase();
  return !!left && !!right && left === right;
}


export async function endAllLiveActivitiesForFarm(farmId, collections = ["orders", "services", "occurrences", "vet"]) {
  try {
    if (!isNativeShell()) return;
    const safeFarmId = normalizeText(farmId);
    if (!safeFarmId) return;
    const globalResult = await endAllLiveActivities({ farmId: safeFarmId, reason: "farm-generation-reset" }).catch(() => null);
    if (globalResult?.supported && (globalResult?.result?.ended || globalResult?.result?.endedCount > 0)) return globalResult;
    const list = Array.isArray(collections) && collections.length ? collections : ["orders", "services", "occurrences", "vet"];
    for (const rawCollection of list) {
      const collection = normalizeText(rawCollection);
      if (!collection) continue;
      const module = COLLECTION_TO_MODULE[collection];
      if (!module) continue;
      await endLiveActivity({
        activityKey: `${safeFarmId}:${collection}:*`,
        farmId: safeFarmId,
        module,
        reason: "farm-generation-reset",
      }).catch(() => {});
      await endLiveActivity({
        activityKey: `${safeFarmId}:${module}:*`,
        farmId: safeFarmId,
        module,
        reason: "farm-generation-reset",
      }).catch(() => {});
    }
    clearBufferedLiveActivityEvents();
  } catch (_error) {}
}

export function reconcileLiveActivitiesAfterPull(farmId, collection, newData, previousData, deps = {}) {
  try {
    if (!isNativeShell()) return;

    const module = COLLECTION_TO_MODULE[collection];
    if (!module) return;
    if (!Array.isArray(newData)) return;

    const { farm, session } = deps || {};
    const sessionName = normalizeText(session?.name || session?.userName || session?.username || session?.email);
    const previousMap = new Map();

    if (Array.isArray(previousData)) {
      for (const previousItem of previousData) {
        const id = readRecordIdentity(previousItem);
        if (id) previousMap.set(id, previousItem);
      }
    }

    const activeGeneration = Number(farm?.activeDataGeneration ?? farm?.farmDataGeneration ?? farm?.dataGeneration ?? farm?.generation ?? 1) || 1;

    for (const item of newData) {
      try {
        if (!item || typeof item !== "object") continue;
        if (item._deleted || item.deletedAt) continue;
        const itemGeneration = Number(item?.dataGeneration ?? item?.farmDataGeneration ?? item?.generation ?? activeGeneration) || activeGeneration;
        if (itemGeneration !== activeGeneration) continue;

        const recordId = readRecordIdentity(item);
        if (!recordId) continue;

        const previous = previousMap.get(recordId) || null;
        const isNew = !previous;
        const currentStatus = readRecordStatus(item);
        const previousStatus = readRecordStatus(previous);
        const statusChanged = !!previous && currentStatus !== previousStatus;
        if (!isNew && !statusChanged) continue;

        const createdBy = readRecordActorName(item, ["createdBy", "created_by", "createdByName", "creatorName", "userName", "user"]);
        if (isNew && sessionName && sameActorName(createdBy, sessionName)) {
          continue;
        }

        // v2.220.08: Native-only reconciliation continua usando a chave canônica do APNs.
        // Para Compras, a chave é sempre farmId:orders:clientId/id — não purchases.
        const keyModule = module === LIVE_ACTIVITY_MODULES.PURCHASES ? "orders" : module;
        const activityKey = `${normalizeText(farmId)}:${keyModule}:${recordId}`;

        if (isTerminalStatusText(currentStatus)) {
          endLiveActivity({ activityKey, module, farmId, recordId }).catch(() => {});
          writeLiveHydrationState(activityKey, null);
          continue;
        }

        // v2.220.08: o reconcile de pull não disputa mais start/update com o servidor/APNs.
        // Ele só guarda um estado local leve para hidratação do deep-link da Live.
        // Nenhum STATUS_CHANGED por pull deve cruzar a bridge nativa, pois isso gerava
        // competição com APNs, duplicidade de updates e risco de 429 no app aberto/background.
        if (!readLiveHydrationTokenChanged(activityKey, currentStatus)) {
          continue;
        }

        writeLiveHydrationState(activityKey, {
          activityKey,
          module,
          farmId,
          recordId,
          status: currentStatus,
          record: item,
          updatedAt: new Date().toISOString(),
        });
      } catch (_itemError) {}
    }
  } catch (_error) {}
}

const LIVE_HYDRATION_KEY = "fz:liveActivities:hydration:v1";

function readLiveHydrationMap() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(LIVE_HYDRATION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function writeLiveHydrationState(activityKey, state) {
  try {
    const key = normalizeText(activityKey);
    if (!key) return;
    const map = readLiveHydrationMap();
    if (state === null) {
      delete map[key];
    } else {
      map[key] = state;
    }
    const entries = Object.entries(map).slice(-60);
    const compactMap = Object.fromEntries(entries);
    if (typeof safeLocalStorageSetJson === "function") {
      safeLocalStorageSetJson(LIVE_HYDRATION_KEY, compactMap, { silent: true });
    } else if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LIVE_HYDRATION_KEY, JSON.stringify(compactMap));
    }
  } catch (_error) {}
}

function readLiveHydrationTokenChanged(activityKey, currentStatus) {
  try {
    const key = normalizeText(activityKey);
    if (!key) return true;
    const map = readLiveHydrationMap();
    const previous = map[key];
    if (!previous) return true;
    return normalizeText(previous.status) !== normalizeText(currentStatus);
  } catch (_error) {
    return true;
  }
}

export function readLiveHydrationState(activityKey) {
  try {
    const key = normalizeText(activityKey);
    if (!key) return null;
    const map = readLiveHydrationMap();
    return map[key] || null;
  } catch (_error) {
    return null;
  }
}

export function getBufferedLiveActivityEvents() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(LIVE_ACTIVITY_EVENT_BUFFER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

export function clearBufferedLiveActivityEvents() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(LIVE_ACTIVITY_EVENT_BUFFER_KEY);
    }
  } catch (_error) {}
}
